import * as Sentry from '@sentry/react';
import { makeNonSerializableStateStore, makeStateStore } from '@playcastdotio/StateStore';
import type { NonSerializableStateStore, StateStore } from '@playcastdotio/CoreTypes';
import { CoordinatorEventImpl } from '../_types/events';
import type { MediaState, MediaEvents } from './stores';
import { TrackCaptureManager } from './managers/TrackCaptureManager';
import { DeviceConfigManager } from './managers/DeviceConfigManager';
import { ShadowcamPresenceMonitor, type ShadowcamHealthSource } from './managers/ShadowcamPresenceMonitor';
import {
  resolveShadowcamDevice,
  waitForShadowcamDevice,
} from './managers/ShadowcamCaptureResolver';
import type { DriverState } from '@playcastdotio/CoreTypes';
import type { GameInstanceSessionRecord } from '../game-instance/GameInstanceStore';
import { resolveInstanceCamera } from './cameraNames';
import { getCaptureResolutionOverride } from '../../api/stores/shared';
import {
  SessionCastManager,
  type SessionCastBinding,
  type SessionCastResult,
  type SessionAudioStatus,
} from './managers/SessionCastManager';

const DIAG = '[AUDIO-BUG-12009:MediaCoordinator]';

// Delay between stopping and reopening a ShadowCam capture. Chromium/DirectShow needs a moment to
// fully release the device; a same-tick reopen reuses the warm capturer at the OLD resolution and
// the DShow pin never renegotiates, so a resolution change would appear to do nothing.
const CAMERA_REOPEN_RELEASE_MS = 700;

type GetDriverStateFn = () => DriverState | Record<string, boolean> | undefined;

/**
 * MediaCoordinator - Owns all media capture and track lifecycle.
 *
 * Replaces the module-level `mediaManager.ts`. Provides a clean coordinator
 * interface for track capture, device management, and track lifecycle events.
 *
 * PeerCoordinator subscribes to `tracksChanged` to push new tracks to peers.
 */
export class MediaCoordinator {
  private mediaStore: NonSerializableStateStore<MediaState>;
  private trackCapture: TrackCaptureManager;
  private deviceConfig: DeviceConfigManager;
  private shadowcamPresenceMonitor: ShadowcamPresenceMonitor;
  private sessionCast: SessionCastManager;
  private getDriverState: GetDriverStateFn;
  private currentShadowcamName: string | null = null;

  public events: MediaEvents = {
    tracksChanged: new CoordinatorEventImpl(),
    deviceListChanged: new CoordinatorEventImpl(),
    videoMuteChanged: new CoordinatorEventImpl(),
    audioMuteChanged: new CoordinatorEventImpl(),
  };

  constructor(getDriverState: GetDriverStateFn = () => undefined) {
    this.getDriverState = getDriverState;
    this.mediaStore = makeNonSerializableStateStore<MediaState>({
      videoTrack: null,
      audioTrack: null,
      videoMuted: false,
      audioMuted: false,
      volume: 1.0,
      selectedCameraId: null,
      selectedMicrophoneId: null,
      selectedDesktopAudioSource: null,
      availableCameras: [],
      availableShadowcams: [],
      availableMicrophones: [],
      availableDesktopAudioSources: [],
    });

    this.trackCapture = new TrackCaptureManager();
    this.deviceConfig = new DeviceConfigManager();
    this.shadowcamPresenceMonitor = new ShadowcamPresenceMonitor(this.deviceConfig, getDriverState);
    this.sessionCast = new SessionCastManager(this.deviceConfig, getDriverState);
  }

  public async init(): Promise<void> {
    console.log('[MediaCoordinator] Initializing...');

    this.trackCapture.setOnTrackChanged((videoTrack, audioTrack) => {
      this.updateTracksInStore(videoTrack, audioTrack);
    });

    this.deviceConfig.setOnDeviceListChanged(() => {
      this.syncDeviceListToStore();
      this.events.deviceListChanged.emit();
      this.shadowcamPresenceMonitor.refreshAfterEnumeration();
    });

    await this.deviceConfig.enumerateDevices();
    this.syncDeviceListToStore();
    this.shadowcamPresenceMonitor.reportLaunchPresence();

    // Subscribe to `devicechange` AFTER the initial enumerate so the launch-time
    // breadcrumb captures the boot snapshot; subsequent plug/enumeration changes
    // (e.g. beacon boot base-camera toggle) still trigger refreshAfterEnumeration.
    this.deviceConfig.startDeviceChangeListener();

    console.log('[MediaCoordinator] Initialized');
  }

  public destroy(): void {
    console.log('[MediaCoordinator] Destroying...');
    this.trackCapture.destroy();
    this.deviceConfig.destroy();
    this.shadowcamPresenceMonitor.destroy();
    this.sessionCast.stopAllSessions();
    console.log('[MediaCoordinator] Destroyed');
  }

  // ========================================
  // Public API - Track Access
  // ========================================

  public getVideoTrack(): MediaStreamTrack | null {
    return this.trackCapture.getVideoTrack();
  }

  public getAudioTrack(): MediaStreamTrack | null {
    return this.trackCapture.getAudioTrack();
  }

  public getVideoTrackForSession(sessionId?: string): MediaStreamTrack | null {
    return sessionId ? this.sessionCast.getVideoTrackForSession(sessionId) : null;
  }

  public getAudioTrackForSession(sessionId?: string): MediaStreamTrack | null {
    return sessionId ? this.sessionCast.getAudioTrackForSession(sessionId) : null;
  }

  public getAudioStatusForSession(sessionId: string): SessionAudioStatus | undefined {
    return this.sessionCast.getAudioStatusForSession(sessionId);
  }

  /**
   * Returns the name of the currently captured shadowcam, or null if none.
   * Used by PeerCoordinator to detect if a re-capture is needed when
   * the incoming peer requires a different shadowcam resolution.
   */
  public getCurrentShadowcamName(): string | null {
    return this.currentShadowcamName;
  }

  // ========================================
  // Public API - Track Capture
  // ========================================

  public async startVideoCapture(deviceId?: string, videoConstraints?: MediaTrackConstraints): Promise<MediaStreamTrack | null> {
    const cameraId = deviceId ?? this.deviceConfig.getSelectedCameraId();
    if (!cameraId) {
      console.warn('[MediaCoordinator] No camera selected for video capture');
      return null;
    }

    this.deviceConfig.selectCamera(cameraId);
    const track = await this.trackCapture.captureVideo({ deviceId: { exact: cameraId }, ...videoConstraints });
    return track;
  }

  public startCastForSession(
    sessionId: string,
    binding: SessionCastBinding,
  ): Promise<SessionCastResult> {
    return this.sessionCast.startCastForSession(sessionId, binding);
  }

  public stopCastForSession(sessionId: string): void {
    this.sessionCast.stopCastForSession(sessionId);
  }

  public async startAudioCapture(deviceId?: string): Promise<MediaStreamTrack | null> {
    const micId = deviceId ?? this.deviceConfig.getSelectedMicrophoneId();
    if (!micId || micId === '0') {
      console.log('[MediaCoordinator] No microphone selected or "None" selected');
      return null;
    }

    this.deviceConfig.selectMicrophone(micId);
    const track = await this.trackCapture.captureAudio({ deviceId: { exact: micId } });
    return track;
  }

  public async startDesktopAudioCapture(): Promise<MediaStreamTrack | null> {
    this.deviceConfig.selectDesktopAudioSource('desktop');
    return await this.trackCapture.captureDesktopAudio();
  }

  /**
   * Capture video from a shadowcam hub route (e.g. the base stream camera).
   * Resolves the Windows pool slot name via driver_status before getUserMedia.
   */
  public async restartShadowcamCapture(shadowcamName?: string): Promise<MediaStreamTrack | null> {
    this.stopVideoCapture();
    // Let the capture device fully release before reopening so a new resolution actually renegotiates.
    await new Promise((resolve) => setTimeout(resolve, CAMERA_REOPEN_RELEASE_MS));
    return this.startShadowcamCapture(shadowcamName);
  }

  public async startShadowcamCapture(shadowcamName?: string): Promise<MediaStreamTrack | null> {
    const existing = this.trackCapture.getVideoTrack();
    if (existing?.readyState === 'live' && shadowcamName && existing.label.includes(shadowcamName)) {
      return existing;
    }

    await this.deviceConfig.enumerateDevices();
    const shadowcams = this.deviceConfig.getShadowcams();

    if (shadowcamName === 'OBS') {
      const deviceId = this.deviceConfig.getCameras().find((c) => c.displayName.includes('OBS'))?.deviceId;
      if (deviceId) {
        return this.startVideoCapture(deviceId);
      }
    }

    if (shadowcams.length === 0) {
      console.warn('[MediaCoordinator] No shadowcams available');
      return null;
    }

    const target = shadowcamName
      ? resolveShadowcamDevice(shadowcamName, shadowcams, this.getDriverState())
      : shadowcams[0];

    if (!target) {
      console.warn(
        `[MediaCoordinator] Shadowcam "${shadowcamName}" not resolved, available:`,
        shadowcams.map((sc) => sc.displayName),
      );
      return null;
    }

    console.log(`[MediaCoordinator] Capturing shadowcam: ${target.displayName} (${target.deviceId}) for hub ${shadowcamName ?? 'default'}`);

    // If this camera has a dev-menu resolution override, open it at that resolution as an exact
    // constraint so Playjector renders to match. If the exact request fails (returns null) fall back
    // to an ideal hint so the camera still opens instead of leaving a black preview.
    const override = getCaptureResolutionOverride(shadowcamName ?? target.displayName);
    const exactConstraints: MediaTrackConstraints | undefined = override
      ? { width: { exact: override.width }, height: { exact: override.height } }
      : undefined;

    let track = await this.startVideoCapture(target.deviceId, exactConstraints);
    if (!track && override) {
      console.warn(`[MediaCoordinator] ${target.displayName} cannot open at exact ${override.width}x${override.height}; retrying with ideal`);
      track = await this.startVideoCapture(target.deviceId, {
        width: { ideal: override.width },
        height: { ideal: override.height },
      });
    }
    if (track) {
      this.currentShadowcamName = shadowcamName ?? target.displayName;
    }
    return track;
  }

  /** Wait until a hub route resolves to an enumerated ShadowCam device (post plug). */
  public async waitForShadowcamDevice(hubRoute: string, timeoutMs = 8_000): Promise<boolean> {
    const device = await waitForShadowcamDevice(
      hubRoute,
      async () => {
        await this.deviceConfig.enumerateDevices();
        return this.deviceConfig.getShadowcams();
      },
      () => this.getDriverState(),
      timeoutMs,
    );
    return device !== null;
  }

  /**
   * Capture audio from AudioCaster virtual device (game audio capture).
   * Uses getUserMedia with the AudioCaster's deviceId.
   */
  public async startGameAudioCapture(): Promise<MediaStreamTrack | null> {
    await this.deviceConfig.enumerateDevices();
    const devices = await navigator.mediaDevices.enumerateDevices();
    const audioInputs = devices.filter((d) => d.kind === 'audioinput');
    const audioInputLabels = audioInputs.map((d) => d.label || '<empty-label>');

    console.log(`${DIAG} Device enumeration: ${audioInputs.length} audioinput(s)`, audioInputLabels);

    const audioCaster = audioInputs.find((d) => d.label.includes('AudioCaster Virtual Device'));

    if (!audioCaster) {
      console.warn(`${DIAG} AudioCaster NOT FOUND among ${audioInputs.length} audioinput devices`, audioInputLabels);
      Sentry.captureMessage('[AUDIO-BUG-12009] AudioCaster virtual device not found', {
        level: 'warning',
        tags: { audio_stage: 'device_lookup', audio_diag: 'AUDIO-BUG-12009' },
        contexts: {
          audioDevices: {
            audioInputCount: audioInputs.length,
            audioInputLabels: audioInputLabels.join(', '),
            emptyLabelCount: audioInputs.filter((d) => !d.label).length,
          },
        },
      });
      return null;
    }

    console.log(`${DIAG} AudioCaster found: "${audioCaster.label}" (${audioCaster.deviceId})`);
    Sentry.addBreadcrumb({
      category: 'AUDIO-BUG-12009',
      message: 'AudioCaster device found, initiating capture',
      level: 'info',
      data: { label: audioCaster.label, deviceId: audioCaster.deviceId, totalAudioInputs: audioInputs.length },
    });

    const track = await this.trackCapture.captureAudio({
      deviceId: { exact: audioCaster.deviceId },
      channelCount: 2,
      autoGainControl: false,
      noiseSuppression: false,
      echoCancellation: false,
    });
    if (!track) {
      console.warn(`${DIAG} captureAudio returned null for AudioCaster deviceId=${audioCaster.deviceId}`);
    }
    return track;
  }

  public stopVideoCapture(): void {
    this.trackCapture.stopVideoTrack();
    this.currentShadowcamName = null;
    this.updateTracksInStore(null, this.trackCapture.getAudioTrack());
  }

  public stopAudioCapture(): void {
    this.trackCapture.stopAudioTrack();
    this.updateTracksInStore(this.trackCapture.getVideoTrack(), null);
  }

  public stopAllCapture(): void {
    this.trackCapture.stopVideoTrack();
    this.trackCapture.stopAudioTrack();
    this.currentShadowcamName = null;
    this.updateTracksInStore(null, null);
  }

  // ========================================
  // Public API - Device Configuration
  // ========================================

  public async refreshDeviceList(): Promise<void> {
    await this.deviceConfig.enumerateDevices();
  }

  public getDeviceConfig(): DeviceConfigManager {
    return this.deviceConfig;
  }

  public hasAudioCaster(): boolean {
    return this.deviceConfig
      .getMicrophones()
      .some((mic) => (mic.label || '').includes('AudioCaster Virtual Device'));
  }

  public getShadowcamPresenceMonitor(): ShadowcamHealthSource {
    return this.shadowcamPresenceMonitor;
  }

  public setRequiredSessionCameras(cameraNames: readonly string[]): void {
    this.shadowcamPresenceMonitor.setRequiredSessionCameras(cameraNames);
  }

  /**
   * Android multi-instance host Phase 5: derive the required session cameras
   * from game instance store records — echo-preferred via
   * `resolveInstanceCamera` (the `instanceLaunched` shadowCamName echo
   * persisted on the record wins over the derived `ShadowCam{k+1:02d}` name).
   */
  public setRequiredInstanceCameras(instances: readonly GameInstanceSessionRecord[]): void {
    this.setRequiredSessionCameras(instances.map((instance) => resolveInstanceCamera(instance)));
  }

  public reportDriverStatus(): void {
    this.shadowcamPresenceMonitor.reportDriverStatus();
  }

  // ========================================
  // Public API - Mute / Volume
  // ========================================

  public setVideoMuted(muted: boolean): void {
    this.deviceConfig.setVideoMuted(muted);
    const videoTrack = this.trackCapture.getVideoTrack();
    if (videoTrack) {
      videoTrack.enabled = !muted;
    }
    this.mediaStore.updateState({
      ...this.mediaStore.currentState,
      videoMuted: muted,
    });
    this.events.videoMuteChanged.emit(muted);
  }

  public setAudioMuted(muted: boolean): void {
    this.deviceConfig.setAudioMuted(muted);
    const audioTrack = this.trackCapture.getAudioTrack();
    if (audioTrack) {
      audioTrack.enabled = !muted;
    }
    this.mediaStore.updateState({
      ...this.mediaStore.currentState,
      audioMuted: muted,
    });
    this.events.audioMuteChanged.emit(muted);
  }

  /**
   * Set capture gain applied to all outgoing audio. `volume` is a linear
   * multiplier (0 = silence, 1.0 = unity, no boost). Drives the GainNode in
   * TrackCaptureManager's Web Audio pipeline so the slider actually changes
   * what peers hear; persists across capture restarts.
   */
  public setVolume(volume: number): void {
    this.deviceConfig.setVolume(volume);
    this.trackCapture.setAudioGain(volume);
    this.mediaStore.updateState({
      ...this.mediaStore.currentState,
      volume,
    });
  }

  // ========================================
  // Internal
  // ========================================

  private updateTracksInStore(videoTrack: MediaStreamTrack | null, audioTrack: MediaStreamTrack | null): void {
    this.mediaStore.updateState({
      ...this.mediaStore.currentState,
      videoTrack,
      audioTrack,
    });
    this.events.tracksChanged.emit({ videoTrack, audioTrack });
  }

  private syncDeviceListToStore(): void {
    this.mediaStore.updateState({
      ...this.mediaStore.currentState,
      availableCameras: this.deviceConfig.getCameras(),
      availableShadowcams: this.deviceConfig.getShadowcams(),
      availableMicrophones: this.deviceConfig.getMicrophones(),
      availableDesktopAudioSources: this.deviceConfig.getDesktopAudioSources(),
    });
  }
}
